import { useEffect, useRef, useState } from "react";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

/**
 * Macro #33 · W8 — Grid zoom-discovery onboarding badge.
 *
 * DISPLAY BEHAVIOUR (Acceptance Fix Batch — UX polish, Claudio):
 *   - Appears on EVERY fresh app/site load where the Grid is presented,
 *     UNLESS the user has explicitly chosen "Don't show again".
 *   - NO auto-dismiss timer. The message STAYS visible until the user acts.
 *   - Zooming does NOT dismiss it (removed — the old hide-on-zoom behaviour).
 *   - X close                → CURRENT occurrence only (no persistence).
 *   - "Don't show again"     → PERSISTENT suppression (localStorage), the ONLY
 *     action that stops it on future loads. No auth required.
 *
 * It is a non-blocking overlay (no modal, no forced interaction). The wrapper
 * is pointer-events:none so it NEVER intercepts Grid gestures (Macro #31 touch
 * ownership preserved); only its own chip/controls are interactive.
 *
 * The optional secondary "entering parcels" micro-hint is UNCHANGED: it is a
 * separate transient confirmation that briefly appears the first time the user
 * crosses the parcel-zoom threshold.
 */

export const ZOOM_HINT_STORAGE_KEY = "grid_zoom_hint_disabled";
const SECONDARY_MS = 2500;
// Mobile toolbar top = 1rem+2.25rem; legend/notice top = 3.75rem+2.25rem (+ safe area).
const MOBILE_BOTTOM = {
  toolbar: "bottom-[calc(4rem+env(safe-area-inset-bottom,0px))]",
  legend: "bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))]",
} as const;

function readDisabled(): boolean {
  try {
    return window.localStorage.getItem(ZOOM_HINT_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function persistDisabled(): void {
  try {
    window.localStorage.setItem(ZOOM_HINT_STORAGE_KEY, "true");
  } catch {
    /* storage unavailable — suppression simply won't persist */
  }
}

function detectPointerMode(): "desktop" | "mobile" {
  try {
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
      return "mobile";
    }
  } catch {
    /* no matchMedia → assume desktop */
  }
  return "desktop";
}

export function ZoomDiscoveryBadge({
  zoomSignal = null,
  parcelZoomThreshold = 4.27,
  pointerMode,
  mobileStack,
  desktopAboveLegend = false,
}: {
  zoomSignal?: number | null;
  parcelZoomThreshold?: number;
  pointerMode?: "desktop" | "mobile";
  /** Defect A — mobile (<768px) only: sit above the bottom toolbar, or above the
   *  zone legend / unavailable notice when it is shown. Unset = desktop layout. */
  mobileStack?: "toolbar" | "legend";
  /** Desktop (>=768px) only: sit above the zone legend / notice (bottom 52px + h-9 + 8px). */
  desktopAboveLegend?: boolean;
}) {
  const { t } = useI18n();
  // Decided once at mount — a permanent suppression means we never render.
  const [disabled] = useState<boolean>(() => readDisabled());
  const [visible, setVisible] = useState<boolean>(() => !readDisabled());
  const [secondary, setSecondary] = useState(false);

  const baselineZoom = useRef<number | null>(null);
  const secondaryShown = useRef(false);
  const mode = pointerMode ?? detectPointerMode();

  // Secondary "entering parcels" micro-hint — UNCHANGED, independent of the
  // persistent badge. Zoom never dismisses the main message (W8 fix).
  useEffect(() => {
    if (disabled) return;
    if (zoomSignal == null) return;
    if (baselineZoom.current == null) {
      baselineZoom.current = zoomSignal;
      return;
    }
    if (!secondaryShown.current && zoomSignal > parcelZoomThreshold) {
      secondaryShown.current = true;
      setSecondary(true);
      window.setTimeout(() => setSecondary(false), SECONDARY_MS);
    }
  }, [zoomSignal, disabled, parcelZoomThreshold]);

  if (disabled) return null;

  const controlHint =
    mode === "mobile" ? t("grid.zoomHint.controlMobile") : t("grid.zoomHint.controlDesktop");

  return (
    <>
      {/* Optional secondary first-zoom feedback — independent of the main badge. */}
      {secondary && (
        <div
          className="pointer-events-none absolute left-1/2 top-4 z-panel -translate-x-1/2"
          aria-hidden
        >
          <span
            data-testid="zoom-enter-parcel-hint"
            className="rounded-pill neon-glass px-3 py-1 font-mono text-[10px] tracking-[0.16em] uppercase text-neon-teal"
          >
            {t("grid.zoomHint.enteringParcels")}
          </span>
        </div>
      )}

      {visible && (
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 z-panel flex justify-center px-4",
            mobileStack ? MOBILE_BOTTOM[mobileStack] : desktopAboveLegend ? "bottom-24" : "bottom-16",
          )}
          data-testid="zoom-discovery-badge"
          data-mobile-stack={mobileStack ?? "none"}
          data-desktop-above-legend={desktopAboveLegend ? "true" : "false"}
        >
          <div
            role="status"
            aria-live="polite"
            className={cn(
              "pointer-events-auto relative flex max-w-[480px] items-center gap-3",
              "rounded-pill neon-glass px-4 py-2.5 shadow-glow-teal-sm",
              // Defect A — <768px: copy takes the full first row, actions wrap below
              // (a pill radius would clip the multi-line copy).
              "max-md:flex-wrap max-md:gap-x-3 max-md:gap-y-2 max-md:rounded-[18px] max-md:px-3.5",
            )}
          >
            <span
              aria-hidden
              className="h-2 w-2 shrink-0 rotate-45 bg-neon-teal shadow-glow-teal-sm"
            />
            <div className="min-w-0 flex-1 max-md:basis-[calc(100%-1.25rem)]">
              <div
                data-testid="zoom-hint-copy"
                className="font-display text-[12.5px] leading-tight text-text-primary"
              >
                {t("grid.zoomHint.copy")}
              </div>
              <div
                data-testid="zoom-hint-control"
                data-pointer-mode={mode}
                className="mt-0.5 font-mono text-[10px] tracking-[0.12em] uppercase text-text-secondary"
              >
                {controlHint}
              </div>
            </div>
            <button
              type="button"
              data-testid="zoom-hint-dont-show"
              onClick={() => {
                persistDisabled();
                setVisible(false);
              }}
              className="shrink-0 whitespace-nowrap max-md:ml-auto rounded-pill border border-border-soft px-2.5 py-1 font-mono text-[9.5px] uppercase tracking-[0.12em] text-text-secondary transition-colors hover:text-text-primary"
            >
              {t("grid.zoomHint.dontShow")}
            </button>
            <button
              type="button"
              data-testid="zoom-hint-close"
              aria-label={t("grid.zoomHint.close")}
              title={t("grid.zoomHint.close")}
              onClick={() => setVisible(false)}
              className="shrink-0 rounded-pill p-1 text-text-secondary transition-colors hover:text-text-primary"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path
                  d="M3 3l6 6M9 3l-6 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
